import {
  IHighlightCommon,
  IHtmlHighlightInput,
  PopoverConfiguration,
  PopoverConfigurationOptions,
  PopoverOption,
  PopoverOptionAskAI,
  PopoverOptionAskAIExternal,
  PopoverOptionComments,
  PopoverOptionCopy,
  PopoverOptionHighlighters,
  PopoverOptionNotes,
  PopoverOptions,
  PopoverOptionTweetHighlight,
} from "../../../typings/annotate/annotation";
import { LayoutSizes } from "../../../typings/annotate/utils";
import { motion } from "framer-motion";
import React, { useEffect, useMemo, useState } from "react";
import { usePopper } from "react-popper";
import AnnotateHighlight from "./annotate-highlight";
import AnnotateAskAI from "./annotate-ask-ai";
import AnnotateNote from "./annotate-note";
import AnnotateComment from "./annotate-comment";
import AnnotateCopy from "./annotate-copy";
import AskingAI from "./asking-ai";
import { Separator } from "@mindstonehq/ui";
import useCommentContext from "../hooks/comment-context";
import { isAndroid, isIOS } from "~/utils/device-check";

export default function AnnotatePopover({
  androidRefElement,
  children,
  closePopover,
  generatedHtml, // Used only to ensure we re-calculate if something went off.
}: React.PropsWithChildren<{
  generatedHtml: any;
  androidRefElement?: any;
  closePopover?: () => void;
}>) {
  const commentContext = useCommentContext();

  const [referenceElement, setReferenceElement] = useState<any | null>();
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>();

  let { styles, attributes, update } = usePopper(androidRefElement || referenceElement, popperElement, {
    placement: androidRefElement || isIOS() || isAndroid() ? "top" : "bottom",
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, androidRefElement ? 20 : 6],
        },
      },
      {
        name: "preventOverflow",
        options: {
          boundary: commentContext.contentRef as HTMLDivElement,
          altBoundary: true,
          rootBoundary: "document",
        },
      },
    ],
  });
  useEffect(() => {
    setTimeout(() => {
      if (update) {
        update();
      }
    }, 100);
  }, [androidRefElement, update]);

  useEffect(() => {
    if (!popperElement || !closePopover) {
      return;
    }
    const onClickListener = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!target) {
        return;
      }
      const headlessUIExisting = document.getElementById("headlessui-portal-root");
      if (
        popperElement &&
        !popperElement.contains(target) &&
        (!headlessUIExisting || !headlessUIExisting.contains(target)) // Not clicking in the Twitter share modal.
      ) {
        closePopover();
      }
    };
    document.addEventListener("mousedown", onClickListener);
    return () => {
      document.removeEventListener("mousedown", onClickListener);
    };
  }, [popperElement]);

  useEffect(() => {
    if (!commentContext.selectedThread) {
      setReferenceElement(null);
    }
    // eslint-disable-next-line lingui/no-unlocalized-strings -- DOM class name
    const elements = document.getElementsByClassName("thread_" + commentContext.selectedThread);
    if (!elements?.length) {
      return;
    }
    const range = new Range();
    Array.from(elements).forEach((e) => range.selectNode(e));
    setReferenceElement({
      getBoundingClientRect: () => {
        return range.getBoundingClientRect();
      },
    });
  }, [commentContext.selectedThread, generatedHtml]);

  return (
    <div className={`group`} ref={setPopperElement} style={styles.popper} {...attributes.popper}>
      {(androidRefElement || referenceElement) && children}
    </div>
  );
}

function findOption<Y, T extends PopoverOption<Y>>(popoverConfiguration: PopoverConfiguration, type: Y) {
  return popoverConfiguration.options.find((x) => x.type == type) as T;
}

export function PopoverInternal({
  highlight,
  popoverConfiguration,
  closePopover,
  layoutSize,
  commentClicked,
  trackMetric,
  removeFakeHighlight,
}: {
  highlight: IHighlightCommon;
  layoutSize?: LayoutSizes;
  closePopover: () => void;
  commentClicked: (highlightID: string) => void;
  popoverConfiguration: PopoverConfiguration;
  trackMetric?: (name: string, data?: { [name: string]: string }) => void;
  removeFakeHighlight?: () => void;
}) {
  popoverConfiguration.options.map((x) => {
    switch (x.type) {
      case PopoverConfigurationOptions.AskAI:
    }
  });
  const [askingAI, setAskingAI] = React.useState(false);
  const askAI = useMemo(
    () =>
      findOption<PopoverConfigurationOptions.AskAI, PopoverOptionAskAI>(
        popoverConfiguration,
        PopoverConfigurationOptions.AskAI,
      ),
    [popoverConfiguration.options],
  );
  const askAIExternal = useMemo(
    () =>
      findOption<PopoverConfigurationOptions.AskAIExternal, PopoverOptionAskAIExternal>(
        popoverConfiguration,
        PopoverConfigurationOptions.AskAIExternal,
      ),
    [popoverConfiguration.options],
  );
  const copy = useMemo(
    () =>
      findOption<PopoverConfigurationOptions.Copy, PopoverOptionCopy>(
        popoverConfiguration,
        PopoverConfigurationOptions.Copy,
      ),
    [popoverConfiguration.options],
  );
  const comments = useMemo(
    () =>
      findOption<PopoverConfigurationOptions.Comments, PopoverOptionComments>(
        popoverConfiguration,
        PopoverConfigurationOptions.Comments,
      ),
    [popoverConfiguration.options],
  );

  const notes = useMemo(
    () =>
      findOption<PopoverConfigurationOptions.Notes, PopoverOptionNotes>(
        popoverConfiguration,
        PopoverConfigurationOptions.Notes,
      ),
    [popoverConfiguration.options],
  );

  const highlighters = useMemo(
    () =>
      findOption<PopoverConfigurationOptions.Highlighters, PopoverOptionHighlighters>(
        popoverConfiguration,
        PopoverConfigurationOptions.Highlighters,
      ),
    [popoverConfiguration.options],
  );
  const tweetHighlight = useMemo(
    () =>
      findOption<PopoverConfigurationOptions.TweetHighlight, PopoverOptionTweetHighlight>(
        popoverConfiguration,
        PopoverConfigurationOptions.TweetHighlight,
      ),
    [popoverConfiguration.options],
  );

  return (
    <motion.div
      layoutId={"annotate_popover"}
      transition={{
        duration: 0.15,
      }}
      className="bg-background flex items-center border shadow-sm rounded-md text-foreground"
    >
      {askingAI ? (
        <AskingAI config={askAI} highlight={highlight} />
      ) : (
        <div className="flex flex-row divide-x">
          <div className="flex divide-x">
            {askAI && <AnnotateAskAI setAskingAI={setAskingAI} />}
            {askAIExternal && (
              <AnnotateAskAI
                setAskingAI={(open) => {
                  askAIExternal.open({
                    highlight: highlight as unknown as IHtmlHighlightInput,
                    context: "",
                    selection: highlight.text,
                  });
                  closePopover();
                }}
              />
            )}
            {highlighters && highlight.canEdit && (
              <AnnotateHighlight config={highlighters} highlight={highlight} closePopover={() => closePopover} />
            )}
            {notes && <AnnotateNote config={notes} highlight={highlight} closePopover={closePopover} />}
          </div>
          <div className="flex divide-x">
            {comments && <AnnotateComment config={comments} highlight={highlight} closePopover={closePopover} />}
            {copy && <AnnotateCopy config={copy} highlight={highlight} closePopover={closePopover} />}
            {/*{tweetHighlight && (*/}
            {/*  <AnnotateTwitter*/}
            {/*    highlight={highlight}*/}
            {/*    config={tweetHighlight}*/}
            {/*    closePopover={closePopover}*/}
            {/*    trackMetric={trackMetric}*/}
            {/*    removeFakeHighlight={removeFakeHighlight}*/}
            {/*  />*/}
            {/*)}*/}
          </div>
        </div>
      )}
    </motion.div>
  );
}
